import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Calendar, Clock, X, ChevronRight, Sparkles, History, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

import { API } from '../config/api';

export default function MesSeances() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState({ upcoming: [], past: [] });
  const [waitlist, setWaitlist] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, waitlistRes, subRes] = await Promise.all([
        axios.get(`${API}/bookings/my`, { withCredentials: true }),
        axios.get(`${API}/waitlist/my`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/my`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setSubscriptionData(subRes.data);
      const now = new Date();
      const upcoming = bookingsRes.data.filter(b => new Date(b.start_time) >= now);
      const past = bookingsRes.data.filter(b => new Date(b.start_time) < now);
      setBookings({ upcoming, past });
      setWaitlist(waitlistRes.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleCancel = async (bookingId) => {
    setActionLoading(bookingId);
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { withCredentials: true });
      toast.success('Réservation annulée');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setActionLoading(null); }
  };

  const handleLeaveWaitlist = async (waitlistId) => {
    setActionLoading(waitlistId);
    try {
      await axios.delete(`${API}/waitlist/${waitlistId}`, { withCredentials: true });
      toast.success('Retiré de la liste d\'attente');
      fetchData();
    } catch (error) { toast.error('Erreur'); }
    finally { setActionLoading(null); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-6 animate-fade-up">
          <h1 className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight mb-1">Mes séances</h1>
          <p className="text-sm text-[#7A6455]">Gérez vos réservations</p>
        </div>

        {/* Subscription Widget */}
        {subscriptionData && (
          <div className="mb-6 animate-fade-up delay-1" data-testid="subscription-quota-widget">
            {subscriptionData.has_subscription ? (
              <Card className="bg-[#493329] text-white rounded-2xl">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#C99A55] text-xs uppercase tracking-widest mb-0.5 font-medium">
                        Membre {subscriptionData.subscription.plan_name}
                      </p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-heading font-medium" data-testid="sessions-remaining-count">
                          {subscriptionData.subscription.sessions_remaining}
                        </span>
                        <span className="text-white/50 text-sm">séances restantes</span>
                      </div>
                    </div>
                    <div>
                      <div className="h-1.5 w-20 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-[#C99A55] rounded-full animate-progress"
                          style={{ '--progress-width': `${Math.min(100, (subscriptionData.subscription.sessions_used / subscriptionData.subscription.sessions_per_month) * 100)}%` }} />
                      </div>
                      <p className="text-xs text-white/30 mt-1 text-right">
                        {subscriptionData.subscription.sessions_used}/{subscriptionData.subscription.sessions_per_month}
                      </p>
                    </div>
                  </div>
                  {subscriptionData.single_class_credits > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#C99A55]" strokeWidth={1.5} />
                      <span className="text-xs text-white/50">+ {subscriptionData.single_class_credits} cours à l'unité</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#F0E8D9] border-none rounded-2xl">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#7A6455]">
                      {subscriptionData.single_class_credits > 0
                        ? `${subscriptionData.single_class_credits} cours à l'unité`
                        : 'Aucun abonnement actif'}
                    </p>
                    <Link to="/abonnement">
                      <Button size="sm" className="bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-full text-xs btn-premium" data-testid="subscribe-cta-btn">
                        Voir les formules <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="upcoming" className="animate-fade-up delay-2">
          <TabsList className="bg-transparent border-b border-[#E9DDCC] rounded-none p-0 mb-6 h-auto w-full">
            <TabsTrigger value="upcoming" className="flex-1 rounded-none border-b-2 border-transparent py-3 data-[state=active]:border-[#C99A55] data-[state=active]:text-[#493329] data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[#7A6455] text-sm font-medium">
              À venir ({bookings.upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="flex-1 rounded-none border-b-2 border-transparent py-3 data-[state=active]:border-[#C99A55] data-[state=active]:text-[#493329] data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[#7A6455] text-sm font-medium">
              Attente ({waitlist.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1 rounded-none border-b-2 border-transparent py-3 data-[state=active]:border-[#C99A55] data-[state=active]:text-[#493329] data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[#7A6455] text-sm font-medium">
              Historique
            </TabsTrigger>
          </TabsList>

          {/* Upcoming */}
          <TabsContent value="upcoming">
            {bookings.upcoming.length === 0 ? (
              <Card className="bg-white border-[#E9DDCC] rounded-2xl">
                <CardContent className="py-14 text-center">
                  <div className="w-14 h-14 rounded-full bg-[#C99A55]/10 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-7 h-7 text-[#C99A55]" strokeWidth={1.5} />
                  </div>
                  <p className="text-[#493329] font-heading font-medium text-lg mb-1">Aucune séance à venir pour le moment.</p>
                  <p className="text-sm text-[#7A6455] mb-5">Réservez votre prochaine séance pour continuer votre progression.</p>
                  <Link to="/reserver">
                    <Button className="bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-full px-6 btn-premium" data-testid="book-empty-cta">
                      Réserver une séance
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {bookings.upcoming.map((b, i) => (
                  <BookingCard key={b.booking_id} booking={b} onCancel={handleCancel} loading={actionLoading} index={i} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Waitlist */}
          <TabsContent value="waitlist">
            {waitlist.length === 0 ? (
              <Card className="bg-white border-[#E9DDCC] rounded-2xl">
                <CardContent className="py-14 text-center">
                  <p className="text-[#7A6455]">Aucune liste d'attente en cours</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {waitlist.map((w) => (
                  <Card key={w.waitlist_id} className="bg-white border-[#E9DDCC] rounded-2xl">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-heading font-medium text-[#493329] mb-1">{w.class_name}</h3>
                          <p className="text-sm text-[#7A6455]">
                            {format(new Date(w.start_time), 'EEEE d MMMM · HH:mm', { locale: fr })}
                          </p>
                          <Badge className="badge-demand rounded-full text-xs mt-2">Position {w.position}</Badge>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleLeaveWaitlist(w.waitlist_id)}
                          disabled={actionLoading === w.waitlist_id} className="text-[#7A6455] hover:text-red-500">
                          {actionLoading === w.waitlist_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" strokeWidth={1.5} />}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* History */}
          <TabsContent value="history">
            {bookings.past.length === 0 ? (
              <Card className="bg-white border-[#E9DDCC] rounded-2xl">
                <CardContent className="py-14 text-center">
                  <History className="w-10 h-10 text-[#E9DDCC] mx-auto mb-4" strokeWidth={1.5} />
                  <p className="text-[#7A6455]">Votre historique apparaîtra ici après vos premières séances.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {bookings.past.slice(0, 20).map((b) => (
                  <div key={b.booking_id} className="flex items-center justify-between p-4 bg-white border border-[#E9DDCC] rounded-2xl">
                    <div>
                      <p className="text-sm font-medium text-[#493329]">{b.class_name}</p>
                      <p className="text-xs text-[#7A6455]">{format(new Date(b.start_time), 'EEEE d MMMM · HH:mm', { locale: fr })}</p>
                    </div>
                    <Badge className={`rounded-full text-xs ${b.status === 'present' ? 'status-present' : 'bg-[#F0E8D9] text-[#7A6455]'}`}>
                      {b.status === 'present' ? 'Complétée' : b.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function BookingCard({ booking: b, onCancel, loading, index }) {
  return (
    <Card className="bg-white border-[#E9DDCC] rounded-2xl card-premium animate-fade-up" style={{ animationDelay: `${index * 0.05}s` }}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading font-medium text-[#493329] mb-1.5">{b.class_name}</h3>
            <div className="flex items-center gap-3 text-sm text-[#7A6455]">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#C99A55]" strokeWidth={1.5} />
                {format(new Date(b.start_time), 'EEEE d MMMM', { locale: fr })}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#C99A55]" strokeWidth={1.5} />
                {format(new Date(b.start_time), 'HH:mm')}
              </span>
            </div>
            {b.instructor_name && <p className="text-xs text-[#AE9885] mt-1">{b.instructor_name}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={() => onCancel(b.booking_id)}
            disabled={loading === b.booking_id} className="text-[#7A6455] hover:text-red-500 hover:bg-red-50">
            {loading === b.booking_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" strokeWidth={1.5} />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
